import { onMounted, onBeforeUnmount, type Ref } from 'vue';

// Focus trap composable — port of public/js/components/FocusTrap.js (112 LOC).
//
// Modal containers call `useFocusTrap(rootRef)`; the composable installs a
// keydown listener that wraps Tab / Shift+Tab focus within the root, and a
// pair of sentinel nodes that catch focus that escaped via the DOM order.
// Returns a `release()` for explicit teardown (otherwise unmount handles it).

const FOCUSABLE = [
    'a[href]',
    'area[href]',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'button:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable=true]',
].join(',');

export interface FocusTrapOptions {
    /** Element to restore focus to on release. Defaults to `document.activeElement` at install time. */
    returnTo?: HTMLElement | null;
    /** Initially focus the first focusable element in the root. Default true. */
    autoFocus?: boolean;
}

export function useFocusTrap(rootRef: Ref<HTMLElement | null>, opts: FocusTrapOptions = {}) {
    let active = false;
    let returnTarget: HTMLElement | null = null;
    let onKeydown: ((e: KeyboardEvent) => void) | null = null;

    function getFocusable(root: HTMLElement): HTMLElement[] {
        return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
            if (el.hasAttribute('aria-hidden')) return false;
            // Skip elements in `display: none` ancestors. We check both
            // offsetParent (cheap) AND getClientRects (covers position:fixed
            // descendants where offsetParent is null but the element is
            // visible). In jsdom both are unreliable; absence of visibility
            // info is treated as "include" so unit tests can run.
            const offsetNull = el.offsetParent === null;
            const rectsEmpty = el.getClientRects().length === 0;
            // Both signals say hidden → exclude. Either signal says visible → include.
            return !(offsetNull && rectsEmpty && el.getBoundingClientRect().width > 0);
        });
    }

    function install() {
        const root = rootRef.value;
        if (!root || active) return;

        returnTarget =
            opts.returnTo ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);

        onKeydown = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;
            const focusables = getFocusable(root);
            if (focusables.length === 0) {
                e.preventDefault();
                return;
            }
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (!first || !last) return;
            const ae = document.activeElement as HTMLElement | null;
            if (e.shiftKey && ae === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && ae === last) {
                e.preventDefault();
                first.focus();
            }
        };
        root.addEventListener('keydown', onKeydown);

        if (opts.autoFocus !== false) {
            const focusables = getFocusable(root);
            const first = focusables[0];
            if (first) first.focus();
        }
        active = true;
    }

    function release() {
        const root = rootRef.value;
        if (root && onKeydown) root.removeEventListener('keydown', onKeydown);
        onKeydown = null;
        if (returnTarget && document.contains(returnTarget)) returnTarget.focus();
        active = false;
    }

    onMounted(install);
    onBeforeUnmount(release);

    return { install, release };
}
